import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { Calendar, AlertCircle } from 'lucide-react';
import { format, parse, isValid, isAfter, getDate } from 'date-fns';
import { useInvoiceStore } from '../stores/invoiceStore';
import { useInputHandling } from '../hooks/useInputHandling';
import { Dialog } from './shared/Dialog';
import { useScheduleStore, useScheduleForInvoice } from '../stores/scheduleStore';
import type { ScheduleType, Schedule } from '@dutchinvoice/types';
import { UNIVERSAL_DATE_FORMAT } from '@dutchinvoice/shared-utils';

const DEFAULT_SCHEDULE: Omit<Schedule, 'templateInvoiceId' | 'id'> = {
  type: 'monthly',
  startDate: '',  // Will be set from invoice date
  active: true
};

const ScheduleEdit: React.FC = () => {
  const { t } = useTranslation();
  const { 
    currentInvoice,
    setEditingSection,
    editingSection,
    updateDate,
    updateInvoice
  } = useInvoiceStore();
  
  const {
    createSchedule,
    updateSchedule,
    deleteSchedule,
    fetchScheduleForInvoice,
    updateScheduleInCache
  } = useScheduleStore();
  
  const isEditing = editingSection?.type === 'schedule';
  const hasSchedule = !!currentInvoice.scheduleId || !!currentInvoice.scheduleData;
  
  // Get schedule from the shared cache
  const cachedSchedule = useScheduleForInvoice(currentInvoice.id);
  
  // Initialize schedule state with default values
  const [scheduleData, setScheduleData] = useState<Omit<Schedule, 'templateInvoiceId' | 'id'>>({
    ...DEFAULT_SCHEDULE,
    startDate: currentInvoice.date
  });

  // Fetch schedule data when component mounts if the invoice has a scheduleId and is not a concept
  useEffect(() => {
    const fetchSchedule = async () => {
      if (isEditing && currentInvoice.status !== 'concept' && currentInvoice.id && currentInvoice.scheduleId) {
        try {
          await fetchScheduleForInvoice(currentInvoice.id);
        } catch (error) {
          console.error(`Error fetching schedule for invoice ${currentInvoice.id}:`, error);
        }
      }
    };
    
    fetchSchedule();
  }, [isEditing, currentInvoice.status, currentInvoice.id, currentInvoice.scheduleId, fetchScheduleForInvoice]);

  // Update schedule data from cache or defaults
  useEffect(() => {
    if (cachedSchedule) {
      // Use data from the cache
      const { type, startDate, endDate, active } = cachedSchedule;
      setScheduleData({ type, startDate, endDate, active });
    } else {
      // Use default values with invoice date
      setScheduleData({
        ...DEFAULT_SCHEDULE,
        startDate: currentInvoice.date
      });
    }
  }, [cachedSchedule, currentInvoice.date]);

  const handleSave = async () => {
    // Sync the invoice date with the schedule start date
    if (currentInvoice.date !== scheduleData.startDate) {
      updateDate(scheduleData.startDate);
    }
    
    try {
      if (currentInvoice.status === 'concept') {
        // For concept invoices, update the invoice with schedule data
        const scheduleWithTemplate = {
          ...scheduleData,
          templateInvoiceId: currentInvoice.id || '',
          id: currentInvoice.scheduleId || `temp-${Date.now()}`
        };
        
        // Update the invoice with scheduleData
        updateInvoice({ 
          scheduleData: scheduleWithTemplate
        });
        
        // Also update the shared cache
        if (currentInvoice.id) {
          updateScheduleInCache(currentInvoice.id, scheduleWithTemplate);
        }
      } else {
        // For non-concept invoices, handle server-side operations
        if (currentInvoice.scheduleId) {
          // Update existing schedule
          await updateSchedule(currentInvoice.scheduleId, scheduleData);
        } else if (currentInvoice.id) {
          // Create new schedule
          const newSchedule = {
            ...scheduleData,
            templateInvoiceId: currentInvoice.id
          };
          const createdSchedule = await createSchedule(newSchedule);
          
          // Update the invoice with the scheduleId
          updateInvoice({ scheduleId: createdSchedule.id });
        }
      }
      
      setEditingSection(null);
    } catch (error) {
      console.error('Failed to save schedule:', error);
      // Handle error (could show a toast notification)
    }
  };

  const handleDelete = async () => {
    if (window.confirm(t('schedule.confirmDelete'))) {
      try {
        if (currentInvoice.status === 'concept') {
          // For concept invoices, just remove the scheduleData
          updateInvoice({ 
            scheduleData: undefined,
            scheduleId: undefined
          });
          
          // Also update the shared cache
          if (currentInvoice.id) {
            updateScheduleInCache(currentInvoice.id, null);
          }
        } else if (currentInvoice.scheduleId) {
          // For non-concept invoices, delete from server
          await deleteSchedule(currentInvoice.scheduleId);
          
          // Remove the scheduleId from the invoice
          updateInvoice({ scheduleId: undefined });
        }
        
        setEditingSection(null);
      } catch (error) {
        console.error('Failed to delete schedule:', error);
        // Handle error (could show a toast notification)
      }
    }
  };

  const handleTypeChange = (type: ScheduleType) => {
    setScheduleData(prev => ({ ...prev, type }));
  };

  const { inputProps: startDateInputProps, handleKeyDown } = useInputHandling({
    onSave: handleSave,
    isEditing,
    value: scheduleData.startDate,
    onChange: (value) => {
      try {
        const newDate = new Date(value);
        if (isValid(newDate)) {
          setScheduleData(prev => ({ 
            ...prev, 
            startDate: format(newDate, UNIVERSAL_DATE_FORMAT)
          }));
        }
      } catch {
        console.error('Error handling date change');
      }
    }
  });

  const { inputProps: endDateInputProps } = useInputHandling({
    onSave: handleSave,
    isEditing,
    value: scheduleData.endDate || '',
    onChange: (value) => {
      try {
        if (!value) {
          setScheduleData(prev => ({ ...prev, endDate: undefined }));
          return;
        }
        
        const newDate = new Date(value);
        if (isValid(newDate)) {
          setScheduleData(prev => ({ 
            ...prev, 
            endDate: format(newDate, UNIVERSAL_DATE_FORMAT)
          }));
        }
      } catch {
        console.error('Error handling date change');
      }
    }
  });

  
  const isEndDateValid = !scheduleData.endDate || isAfter(
    parse(scheduleData.endDate, UNIVERSAL_DATE_FORMAT, new Date()),
    parse(scheduleData.startDate, UNIVERSAL_DATE_FORMAT, new Date())
  );

  // Get the day from the start date for display
  const startDateDay = isValid(parse(scheduleData.startDate, UNIVERSAL_DATE_FORMAT, new Date())) 
    ? getDate(parse(scheduleData.startDate, UNIVERSAL_DATE_FORMAT, new Date())) 
    : 1;

  return (
    <Dialog
      isOpen={isEditing}
      onClose={() => setEditingSection(null)}
      size="md"
    >
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="p-6 space-y-6" onKeyDown={handleKeyDown}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {hasSchedule ? t('schedule.edit') : t('schedule.create')}
            </h2>
          </div>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('schedule.type')}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {(['monthly', 'quarterly', 'yearly'] as ScheduleType[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => handleTypeChange(type)}
                    className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                      scheduleData.type === type
                        ? 'bg-primary-50 text-primary-700 font-medium ring-1 ring-primary-200'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    {t(`schedule.${type}`)}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('schedule.startDate')}
              </label>
              <div className="relative">
                <input
                  type="date"
                  {...startDateInputProps}
                  className="block w-full pl-10 pr-3 py-2 text-sm border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-gray-900"
                  style={{
                    colorScheme: 'light'
                  }}
                />
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {t('schedule.dayFromStartDate', { day: startDateDay })}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {t('schedule.dateSync')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('schedule.endDate')} <span className="text-gray-400 text-xs">({t('schedule.optional')})</span>
              </label>
              <div className="relative">
                <input
                  type="date"
                  {...endDateInputProps}
                  className={`block w-full pl-10 pr-3 py-2 text-sm border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-gray-900 ${
                    !isEndDateValid ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                  }`}
                  style={{
                    colorScheme: 'light'
                  }}
                />
                <Calendar className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${
                  !isEndDateValid ? 'text-red-400' : 'text-gray-400'
                }`} />
              </div>
              {!isEndDateValid && (
                <p className="mt-1.5 text-xs font-medium text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {t('schedule.endDateError')}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            {hasSchedule && (
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
              >
                {t('actions.delete')}
              </button>
            )}
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => setEditingSection(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('actions.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={!isEndDateValid}
                className={`px-4 py-2 text-white text-sm font-medium rounded-lg transition-colors ${
                  isEndDateValid 
                    ? 'bg-primary-500 hover:bg-primary-600' 
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                {t('actions.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ScheduleEdit; 